import * as React from "react";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Rating,
  Stack,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import StarIcon from "@mui/icons-material/Star";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useNavigate } from "react-router-dom";
import { Project, ProjectResource } from "../../types/project";
import { Talent } from "../../types/users";
import { nl2br } from "../../utils/helper";
import { useGetTalentByIdQuery } from "../../services/talent.service";
import { formatMoney } from "../../utils/helper";
import { getDistanceBetweenPoints } from "../../utils/mapHelper";
import { GlassDrawer } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";

interface ProjectResourceDrawerProps {
  data: ProjectResource | null;
  dataProject: Project;
  forceRefresh?: boolean;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ProjectResourceDrawer(
  props: ProjectResourceDrawerProps
) {
  const { data, dataProject, forceRefresh = false, open, setOpen } = props;
  const navigate = useNavigate();
  const rateLabel = useUILabel("project.rate");
  const maxHourLabel = useUILabel("project.maxHour");
  const moreDetailLabel = useUILabel("project.moreDetail");

  const projectCoordinate = React.useMemo(() => {
    console.log("dataProject", dataProject);
    let _coordinates: number[] = [];
    if (!!dataProject?.customer) {
      _coordinates = dataProject?.customer?.coordinate?.coordinates;
    } else if (!!dataProject?.location) {
      _coordinates = dataProject?.location?.coordinate?.coordinates || [];
    }
    if (_coordinates.length > 0) {
      return { lat: _coordinates[1], lng: _coordinates[0] };
    } else return undefined;
  }, [dataProject]);

  console.log("projectCoordinate", projectCoordinate);

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setOpen(open);
    };

  const list = () => (
    <>
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          justifyContent: "flex-end",
          mt: { xs: 0.5, sm: 1.5 },
        }}
      >
        <ClearIcon
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
          sx={{ cursor: "pointer" }}
        />
      </Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ px: { xs: 0.5, sm: 1 } }}
      >
        <Avatar
          alt={`${data?.resource?.first_name}`}
          src={`${
            data?.resource?.photo?.[0]?.original_url || "broken-image.jpg"
          }`}
          sx={{ width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 } }}
        />
        <Box>
          <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "#12903C" }}>
            {data?.resource?.full_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
            {data?.resource?.city}, {data?.resource?.state}{" "}
            {data?.resource?.zip}
          </Typography>
          <Rating
            value={data?.resource?.total_rating || 0}
            readOnly
            precision={0.5}
            size="small"
            sx={{ mt: { xs: 0.5, sm: 0 } }}
          />
        </Box>
      </Stack>
      <Divider />

      <List
        sx={{
          "& .MuiListItem-root": {
            px: { xs: 0.5, sm: 1 },
          },
        }}
      >
        <ListItem disableGutters>
          <ListItemText
            primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
            primary={rateLabel}
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {`$${formatMoney(+Number(data?.rate || 0))}`}
                </Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem disableGutters>
          <ListItemText
            primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
            primary={maxHourLabel}
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {`${data?.max_hour || 0} hours`}
                </Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        {/* <ListItem disableGutters>
          <ListItemText
            primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 } }}
            secondaryTypographyProps={{ fontSize: { xs: 12, sm: 14 } }}
            primary="Distance From Site"
            secondary={
              !!data?.resource?.coordinate
                ? `${+Number(
                    getDistanceBetweenPoints(
                      {
                        lat: data?.resource?.coordinate?.coordinates[1] || 0,
                        lng: data?.resource?.coordinate?.coordinates[0] || 0,
                      },
                      !!projectCoordinate
                        ? projectCoordinate
                        : { lat: 0, lng: 0 }
                    )
                  ).toFixed(2)} miles`
                : `N/A`
            }
          />
        </ListItem> */}
        <Divider />
      </List>
        <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
        <Button
          variant="contained"
            fullWidth
          onClick={() => navigate(`/talents/${data?.resource_id}`)}
        >
          {moreDetailLabel}
        </Button>
      </Box>
      </>
  );

  return (
    <GlassDrawer
      anchor="right"
      open={open}
      onClose={toggleDrawer(false)}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420, md: 460 },
        },
      }}
      contentProps={{ role: "presentation" }}
    >
          {list()}
    </GlassDrawer>
  );
}
